import React from 'react'
import { FastField, Form, Formik, FormikHelpers, FormikProps } from 'formik'
import * as Yup from 'yup'
import { Channel } from 'twilio-chat'
import toast from 'react-hot-toast'
import { faCamera } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import FormikInput from '../FormikInput'
import ImagePreview from './ImagePreview'
import FilePreview from './FilePreview'

type MessageFormProps = {
  channel: Channel
}
type MessageDTO = {
  text: string
  file: File | null | ''
}

const initialValues: MessageDTO = {
  text: '',
  file: '',
}

const schema = Yup.object().shape({
  text: Yup.string(),
  file: Yup.mixed(),
})

const MessageForm: React.FC<MessageFormProps> = (props) => {
  const handleOnSubmit = async (value: MessageDTO, formikBag: FormikHelpers<MessageDTO>) => {
    try {
      if (value.text) {
        await props.channel.sendMessage(value.text)
      }

      if (value.file) {
        const formData = new FormData()
        formData.append('file', value.file)
        await props.channel.sendMessage(formData)
      }
      formikBag.resetForm()
    } catch {
      toast.error('Something went wrong, if the error persist please contact support.')
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleOnSubmit}
    >
      {({ isSubmitting, values, setFieldValue }: FormikProps<MessageDTO>) => {
        return (
          <Form className="w-full flex-col border-t border-gray-300 p-3">
            <div>
              {values.file && ['image/jpeg', 'image/jpg', 'image/gif', 'image/png'].includes(values.file.type) ? (
                <ImagePreview file={values.file} onDelete={() => setFieldValue('file', '')} />
              ) : (
                <FilePreview file={values.file} onDelete={() => setFieldValue('file', '')} />
              )}
            </div>
            <div className="flex w-full items-center justify-between">
              <fieldset>
                <label
                  htmlFor="file"
                  className="flex h-8 w-8 cursor-pointer items-center rounded-full bg-brand-100 p-2"
                >
                  <FontAwesomeIcon className="h-5 w-5 text-brand-500" icon={faCamera} />
                </label>
                <input
                  type="file"
                  name="file"
                  id="file"
                  className="hidden"
                  onChange={(event) => {
                    // @ts-ignore
                    setFieldValue('file', event?.currentTarget?.files[0])
                  }}
                />
              </fieldset>
              <fieldset className="mx-3 w-full" disabled={isSubmitting}>
                <FastField type="text" name="text" component={FormikInput} placeholder="Type your message here" />
              </fieldset>

              <button type="submit" disabled={isSubmitting}>
                <svg
                  className="h-5 w-5 origin-center rotate-90 transform text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </div>
          </Form>
        )
      }}
    </Formik>
  )
}

export default MessageForm
